import { FolderOpen, Loader2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Case } from '../../types/case-management'
import type { ClientProfileWithCaseCount } from '../../types/case-management'

interface CaseListItemProps {
  caseItem: Case
  selectedCaseToLink: string
  setSelectedCaseToLink: (id: string) => void
}

function CaseListItem(props: CaseListItemProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { caseItem, selectedCaseToLink, setSelectedCaseToLink } = props
  const isSelected = selectedCaseToLink === caseItem.id
  const selectedClass = isSelected ? 'bg-[#4e5e82]/5 dark:bg-[#8a9bba]/10' : ''
  return (
    <label
      className={`flex cursor-pointer items-center gap-3 border-b border-stone-100 px-4 py-3 last:border-b-0 hover:bg-stone-50 dark:border-stone-800 dark:hover:bg-stone-800/50 ${selectedClass}`}
    >
      <input
        type="radio"
        name="caseToLink"
        checked={isSelected}
        onChange={() => {
          setSelectedCaseToLink(caseItem.id)
        }}
        className="h-4 w-4 text-[#4e5e82]"
      />
      <div className="flex-1">
        <p className="font-medium text-stone-900 dark:text-stone-100">{caseItem.name}</p>
        <p className="text-xs text-stone-500 dark:text-stone-400">
          {caseItem.orgCaseReference ?? t('clients.noReference')} - {caseItem.status}
        </p>
      </div>
    </label>
  )
}

interface CaseListProps {
  unlinkedCases: Case[]
  selectedCaseToLink: string
  setSelectedCaseToLink: (id: string) => void
}

function CaseList(props: CaseListProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { unlinkedCases, selectedCaseToLink, setSelectedCaseToLink } = props
  return (
    <>
      <p className="mb-3 text-sm text-stone-600 dark:text-stone-400">
        {t('clients.selectCaseToLink')}
      </p>
      <div className="max-h-64 overflow-y-auto rounded-lg border border-stone-200 dark:border-stone-700">
        {unlinkedCases.map((caseItem) => (
          <CaseListItem
            key={caseItem.id}
            caseItem={caseItem}
            selectedCaseToLink={selectedCaseToLink}
            setSelectedCaseToLink={setSelectedCaseToLink}
          />
        ))}
      </div>
    </>
  )
}

function NoUnlinkedCases(): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="py-8 text-center">
      <FolderOpen className="mx-auto h-12 w-12 text-stone-300 dark:text-stone-600" />
      <p className="mt-3 text-sm text-stone-500 dark:text-stone-400">
        {t('clients.noUnlinkedCases')}
      </p>
      <p className="mt-1 text-xs text-stone-400 dark:text-stone-500">
        {t('clients.allCasesLinked')}
      </p>
    </div>
  )
}

interface ContentProps {
  loading: boolean
  unlinkedCases: Case[]
  selectedCaseToLink: string
  setSelectedCaseToLink: (id: string) => void
}

function ModalContent(props: ContentProps): React.ReactElement {
  const { loading, unlinkedCases, selectedCaseToLink, setSelectedCaseToLink } = props
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#4e5e82]" />
      </div>
    )
  }
  if (unlinkedCases.length === 0) return <NoUnlinkedCases />
  return (
    <CaseList
      unlinkedCases={unlinkedCases}
      selectedCaseToLink={selectedCaseToLink}
      setSelectedCaseToLink={setSelectedCaseToLink}
    />
  )
}

export interface LinkCaseModalProps {
  selectedClient: ClientProfileWithCaseCount
  unlinkedCases: Case[]
  unlinkedCasesLoading: boolean
  selectedCaseToLink: string
  setSelectedCaseToLink: (id: string) => void
  formError: string | null
  formLoading: boolean
  onClose: () => void
  onConfirm: () => void
}

export function LinkCaseModal(props: LinkCaseModalProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { selectedClient, unlinkedCases, unlinkedCasesLoading, selectedCaseToLink } = props
  const { setSelectedCaseToLink, formError, formLoading, onClose, onConfirm } = props
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-stone-900">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
            {t('clients.linkCaseTitle', { name: selectedClient.name })}
          </h2>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {formError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-900/20">
            <p className="text-sm text-red-700 dark:text-red-400">{formError}</p>
          </div>
        )}
        <ModalContent
          loading={unlinkedCasesLoading}
          unlinkedCases={unlinkedCases}
          selectedCaseToLink={selectedCaseToLink}
          setSelectedCaseToLink={setSelectedCaseToLink}
        />
        <div className="mt-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-stone-200 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 dark:border-stone-700 dark:text-stone-300 dark:hover:bg-stone-800"
          >
            {t('actions.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={formLoading || !selectedCaseToLink}
            className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {formLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('clients.linkCase')}
          </button>
        </div>
      </div>
    </div>
  )
}
